import type { SDKState } from "@/state/sdk-state";
import { SimpleObservable, SimpleObservableSubject } from "@/utils/observable";
import * as z from "zod/v4-mini";
import type { Logger } from "@/utils/logger";

import {
  ASYNC_FLOW_MESSAGE_EVENT_NAME,
  AsyncFlowMessageType,
} from "@f3/shared/async-flow-messages";

export { AsyncFlowMessageType };

const validateAuthenticationSuccessSchema = z.object({
  type: z.literal(AsyncFlowMessageType.ValidateAuthenticationSuccess),
  code: z.string(),
  state: z.string(),
  redirect_uri: z.string(),
});

const validateAuthenticationErrorSchema = z.object({
  type: z.literal(AsyncFlowMessageType.ValidateAuthenticationError),
  error: z.string(),
});

const authModalSuccessSchema = z.object({
  type: z.literal(AsyncFlowMessageType.AuthModalSuccess),
  sessionToken: z.string(),
  user: z.optional(z.object({ id: z.string(), username: z.string() })),
  authChannel: z.enum(["otp", "oauth"]),
});

const authModalClosedSchema = z.object({
  type: z.literal(AsyncFlowMessageType.AuthModalClosed),
});

const asyncFlowMessageSchema = z.object({
  type: z.literal(ASYNC_FLOW_MESSAGE_EVENT_NAME),
  data: z.discriminatedUnion("type", [
    validateAuthenticationSuccessSchema,
    validateAuthenticationErrorSchema,
    authModalSuccessSchema,
    authModalClosedSchema,
  ]),
});

export type AuthIframeManagerAuthEvent = z.infer<typeof asyncFlowMessageSchema>;

export class AuthIframeBroker extends SimpleObservable {
  #state: SDKState;
  #logger: Logger;
  authObservable: SimpleObservableSubject<AuthIframeManagerAuthEvent>;

  constructor(
    state: SDKState,
    logger: Logger,
    authObservable: SimpleObservableSubject<AuthIframeManagerAuthEvent> = new SimpleObservableSubject<AuthIframeManagerAuthEvent>(),
  ) {
    super();
    this.#state = state;
    this.authObservable = authObservable;
    this.#logger = logger.extend(this.constructor.name);
  }

  handleMessage = (event: MessageEvent) => {
    if (event.origin !== this.#state.options.appOrigin) return;
    if (event.data.type !== ASYNC_FLOW_MESSAGE_EVENT_NAME) return;

    const result = asyncFlowMessageSchema.safeParse(event.data);

    if (result.success) {
      this.notify(this.authObservable, result.data);
    } else {
      this.#logger.error(
        "Received message with invalid data",
        result.error,
        event,
      );
    }
  };

  init() {
    window.addEventListener("message", this.handleMessage);
  }

  detach() {
    window.removeEventListener("message", this.handleMessage);
    this.authObservable.clear();
  }

  sendMessage(message: AuthIframeManagerAuthEvent) {
    window.postMessage(message, this.#state.options.appOrigin);
  }
}
